/**
 * @file NoticesWidget.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-11-03
 * @description Dashboard "Notices" widget (Cam, 14 Oct 2026): things that need
 *              someone's attention, shown to the people who can act on them.
 *
 * The server decides everything — which notices this person sees (by role /
 * permission, config/noticeRegistry.js), what each one finds and how its rows
 * read (GET /api/notices). This widget only renders them, generically, so a
 * new notice needs no change here.
 *
 * 2.0.0 (2026-10-15, Cam): the same notices are the status bar's Alerts. The
 * list is the shared NoticeList (also the alerts window's), read by
 * useNotices: each row can be acknowledged — just for you — and the list
 * re-reads the moment the live alert signature moves (the status bar's
 * 20-second poll), so an issue fixed by anyone leaves every screen within a
 * poll. The header badge counts what YOU have not acknowledged.
 *
 * 2.1.0 (2026-11-03): persistent alerts (no acknowledging) and a row's own
 * actions — NoticeList 1.1.0.
 */

import usePersistedState from '../hooks/usePersistedState';
import useNotices from '../hooks/useNotices';
import NoticeList, { unacknowledgedTotal } from '../components/NoticeList';
import './NoticesWidget.css';

function NoticesWidget() {
  const { data, loading, failed, load, acknowledge, undo, busy, error, runAction, actionBusy, info } = useNotices();
  const [collapsed, setCollapsed] = usePersistedState('dashboardNotices.collapsed', {});
  const total = unacknowledgedTotal(data?.notices);
  const toggle = (key) => setCollapsed(prev => ({ ...(prev || {}), [key]: !(prev || {})[key] }));

  return (
    <div className="dashboard-card notices-card">
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">campaign</span>
          Notices
        </h3>
        {total > 0 && <span className="notices-total-badge" title="Things needing attention, not yet acknowledged by you">{total}</span>}
      </div>
      <div className="card-body">
        <NoticeList
          data={data}
          loading={loading}
          failed={failed}
          onRetry={load}
          collapsed={collapsed || {}}
          onToggle={toggle}
          onAcknowledge={acknowledge}
          onUndo={undo}
          busy={busy}
          error={error}
          onAction={runAction}
          actionBusy={actionBusy}
          info={info}
        />
      </div>
    </div>
  );
}

export default NoticesWidget;
